/**
 * Fund constituents — top-N-by-weight holdings inside common ETFs and
 * mutual funds. Phase 11 (2026-05-09).
 *
 * Manually curated snapshot of the ~30 funds Andre's household holds (or
 * comparators they might switch to). v1 captures the top-10-by-weight
 * positions per fund. The "look-through" / holdings-overlap helper
 * computes effective per-ticker exposure: if you hold $10k VOO and AAPL
 * is 7% of VOO, you have $700 indirect AAPL exposure on top of any
 * direct AAPL position.
 *
 * **Weight units:** basis points (100 bp = 1%). Fund-level weights need
 * not sum to 10000 — top-10 typically covers 25–35% of an S&P-500 ETF.
 * The helper uses these weights as-is; uncovered weight is treated as
 * "diversified residual" (no concentration contribution).
 *
 * **as_of**: snapshot date. Helpers can surface a stale warning when
 * the snapshot is more than ~6 months old. Refresh cadence target is
 * twice yearly; Phase 12+ may pull from a paid feed.
 *
 * **Sources** (issuer fund-fact sheets, accessed 2026-05-09):
 *   - Vanguard funds (VTI, VOO, VXUS, BND, BNDX, VTIP, VB, VTV, VUG):
 *       https://investor.vanguard.com/investment-products
 *   - iShares (ITOT, IVV, AGG): https://www.ishares.com/
 *   - Fidelity (FXAIX, FZROX, FZILX, FSPSX): https://fundresearch.fidelity.com/
 *   - State Street (SPY): https://www.ssga.com/us/en/individual/etfs/spy
 *   - Invesco (QQQ): https://www.invesco.com/qqq-etf/en/about/qqq-etf.html
 *   - Schwab (SCHB, SCHX, SCHF): https://www.schwabassetmanagement.com/
 */

export interface FundConstituent {
  /** Underlying ticker (e.g. "AAPL", "MSFT"). */
  readonly ticker: string;
  /** Weight inside the parent fund, in basis points. */
  readonly weight_bp: number;
}

export interface FundConstituentEntry {
  /** Fund ticker (e.g. "VOO", "VTI"). */
  readonly fund_ticker: string;
  /** Display name. */
  readonly name: string;
  /** "etf" or "mutual_fund" for UI grouping; informational only. */
  readonly fund_type: "etf" | "mutual_fund";
  /** Top-N (typically 10) holdings, descending by weight. */
  readonly top_holdings: readonly FundConstituent[];
}

export const FUND_CONSTITUENTS_AS_OF = "2026-05-09";

/**
 * Top-10 holdings as of the AS_OF date for funds Andre's household
 * holds + popular comparators. Weights are issuer-published latest
 * snapshots.
 */
export const FUND_CONSTITUENTS_TABLE: readonly FundConstituentEntry[] = [
  // === Total Stock Market =================================================
  {
    fund_ticker: "VTI",
    name: "Vanguard Total Stock Market ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 620 },
      { ticker: "MSFT", weight_bp: 580 },
      { ticker: "NVDA", weight_bp: 520 },
      { ticker: "AMZN", weight_bp: 360 },
      { ticker: "META", weight_bp: 240 },
      { ticker: "GOOGL", weight_bp: 200 },
      { ticker: "GOOG", weight_bp: 170 },
      { ticker: "TSLA", weight_bp: 160 },
      { ticker: "BRK.B", weight_bp: 140 },
      { ticker: "AVGO", weight_bp: 130 },
    ],
  },
  {
    fund_ticker: "ITOT",
    name: "iShares Core S&P Total US Stock Market ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 620 },
      { ticker: "MSFT", weight_bp: 590 },
      { ticker: "NVDA", weight_bp: 520 },
      { ticker: "AMZN", weight_bp: 360 },
      { ticker: "META", weight_bp: 240 },
      { ticker: "GOOGL", weight_bp: 200 },
      { ticker: "GOOG", weight_bp: 170 },
      { ticker: "TSLA", weight_bp: 160 },
      { ticker: "BRK.B", weight_bp: 140 },
      { ticker: "AVGO", weight_bp: 130 },
    ],
  },
  {
    fund_ticker: "VTSAX",
    name: "Vanguard Total Stock Market Index Fund (Admiral)",
    fund_type: "mutual_fund",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 620 },
      { ticker: "MSFT", weight_bp: 580 },
      { ticker: "NVDA", weight_bp: 520 },
      { ticker: "AMZN", weight_bp: 360 },
      { ticker: "META", weight_bp: 240 },
      { ticker: "GOOGL", weight_bp: 200 },
      { ticker: "GOOG", weight_bp: 170 },
      { ticker: "TSLA", weight_bp: 160 },
      { ticker: "BRK.B", weight_bp: 140 },
      { ticker: "AVGO", weight_bp: 130 },
    ],
  },
  {
    fund_ticker: "FZROX",
    name: "Fidelity ZERO Total Market Index Fund",
    fund_type: "mutual_fund",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 615 },
      { ticker: "MSFT", weight_bp: 575 },
      { ticker: "NVDA", weight_bp: 510 },
      { ticker: "AMZN", weight_bp: 355 },
      { ticker: "META", weight_bp: 235 },
      { ticker: "GOOGL", weight_bp: 195 },
      { ticker: "GOOG", weight_bp: 165 },
      { ticker: "TSLA", weight_bp: 155 },
      { ticker: "BRK.B", weight_bp: 135 },
      { ticker: "AVGO", weight_bp: 125 },
    ],
  },
  // === S&P 500 ============================================================
  {
    fund_ticker: "VOO",
    name: "Vanguard S&P 500 ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 700 },
      { ticker: "MSFT", weight_bp: 660 },
      { ticker: "NVDA", weight_bp: 600 },
      { ticker: "AMZN", weight_bp: 410 },
      { ticker: "META", weight_bp: 270 },
      { ticker: "GOOGL", weight_bp: 230 },
      { ticker: "GOOG", weight_bp: 195 },
      { ticker: "TSLA", weight_bp: 180 },
      { ticker: "BRK.B", weight_bp: 165 },
      { ticker: "AVGO", weight_bp: 150 },
    ],
  },
  {
    fund_ticker: "SPY",
    name: "SPDR S&P 500 ETF Trust",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 700 },
      { ticker: "MSFT", weight_bp: 660 },
      { ticker: "NVDA", weight_bp: 600 },
      { ticker: "AMZN", weight_bp: 410 },
      { ticker: "META", weight_bp: 270 },
      { ticker: "GOOGL", weight_bp: 230 },
      { ticker: "GOOG", weight_bp: 195 },
      { ticker: "TSLA", weight_bp: 180 },
      { ticker: "BRK.B", weight_bp: 165 },
      { ticker: "AVGO", weight_bp: 150 },
    ],
  },
  {
    fund_ticker: "IVV",
    name: "iShares Core S&P 500 ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 700 },
      { ticker: "MSFT", weight_bp: 660 },
      { ticker: "NVDA", weight_bp: 600 },
      { ticker: "AMZN", weight_bp: 410 },
      { ticker: "META", weight_bp: 270 },
      { ticker: "GOOGL", weight_bp: 230 },
      { ticker: "GOOG", weight_bp: 195 },
      { ticker: "TSLA", weight_bp: 180 },
      { ticker: "BRK.B", weight_bp: 165 },
      { ticker: "AVGO", weight_bp: 150 },
    ],
  },
  {
    fund_ticker: "FXAIX",
    name: "Fidelity 500 Index Fund",
    fund_type: "mutual_fund",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 700 },
      { ticker: "MSFT", weight_bp: 660 },
      { ticker: "NVDA", weight_bp: 600 },
      { ticker: "AMZN", weight_bp: 410 },
      { ticker: "META", weight_bp: 270 },
      { ticker: "GOOGL", weight_bp: 230 },
      { ticker: "GOOG", weight_bp: 195 },
      { ticker: "TSLA", weight_bp: 180 },
      { ticker: "BRK.B", weight_bp: 165 },
      { ticker: "AVGO", weight_bp: 150 },
    ],
  },
  // === Nasdaq 100 / Tech-tilt ============================================
  {
    fund_ticker: "QQQ",
    name: "Invesco QQQ Trust (Nasdaq-100)",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 900 },
      { ticker: "MSFT", weight_bp: 870 },
      { ticker: "NVDA", weight_bp: 770 },
      { ticker: "AMZN", weight_bp: 540 },
      { ticker: "META", weight_bp: 470 },
      { ticker: "AVGO", weight_bp: 410 },
      { ticker: "GOOGL", weight_bp: 280 },
      { ticker: "GOOG", weight_bp: 270 },
      { ticker: "TSLA", weight_bp: 260 },
      { ticker: "COST", weight_bp: 240 },
    ],
  },
  {
    fund_ticker: "VUG",
    name: "Vanguard Growth ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 1130 },
      { ticker: "MSFT", weight_bp: 1080 },
      { ticker: "NVDA", weight_bp: 990 },
      { ticker: "AMZN", weight_bp: 670 },
      { ticker: "META", weight_bp: 440 },
      { ticker: "GOOGL", weight_bp: 380 },
      { ticker: "GOOG", weight_bp: 320 },
      { ticker: "TSLA", weight_bp: 290 },
      { ticker: "AVGO", weight_bp: 260 },
      { ticker: "LLY", weight_bp: 200 },
    ],
  },
  // === Value tilt ========================================================
  {
    fund_ticker: "VTV",
    name: "Vanguard Value ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "BRK.B", weight_bp: 320 },
      { ticker: "JPM", weight_bp: 270 },
      { ticker: "XOM", weight_bp: 240 },
      { ticker: "JNJ", weight_bp: 195 },
      { ticker: "WMT", weight_bp: 175 },
      { ticker: "PG", weight_bp: 175 },
      { ticker: "V", weight_bp: 165 },
      { ticker: "BAC", weight_bp: 145 },
      { ticker: "UNH", weight_bp: 145 },
      { ticker: "HD", weight_bp: 145 },
    ],
  },
  // === Small / Mid cap ===================================================
  {
    fund_ticker: "VB",
    name: "Vanguard Small-Cap ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "EME", weight_bp: 65 },
      { ticker: "ATR", weight_bp: 55 },
      { ticker: "WSM", weight_bp: 50 },
      { ticker: "EXP", weight_bp: 50 },
      { ticker: "RGA", weight_bp: 45 },
      { ticker: "BMRN", weight_bp: 45 },
      { ticker: "LSTR", weight_bp: 45 },
      { ticker: "UNM", weight_bp: 40 },
      { ticker: "WTRG", weight_bp: 40 },
      { ticker: "CHRW", weight_bp: 40 },
    ],
  },
  // === International =====================================================
  {
    fund_ticker: "VXUS",
    name: "Vanguard Total International Stock ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "TSM", weight_bp: 220 },
      { ticker: "ASML", weight_bp: 95 },
      { ticker: "NVO", weight_bp: 90 },
      { ticker: "NESN.SW", weight_bp: 85 },
      { ticker: "TM", weight_bp: 70 },
      { ticker: "ROG.SW", weight_bp: 70 },
      { ticker: "SAP", weight_bp: 70 },
      { ticker: "AZN", weight_bp: 65 },
      { ticker: "SHEL", weight_bp: 65 },
      { ticker: "NVS", weight_bp: 60 },
    ],
  },
  {
    fund_ticker: "VTIAX",
    name: "Vanguard Total International Stock Index Fund",
    fund_type: "mutual_fund",
    top_holdings: [
      { ticker: "TSM", weight_bp: 220 },
      { ticker: "ASML", weight_bp: 95 },
      { ticker: "NVO", weight_bp: 90 },
      { ticker: "NESN.SW", weight_bp: 85 },
      { ticker: "TM", weight_bp: 70 },
      { ticker: "ROG.SW", weight_bp: 70 },
      { ticker: "SAP", weight_bp: 70 },
      { ticker: "AZN", weight_bp: 65 },
      { ticker: "SHEL", weight_bp: 65 },
      { ticker: "NVS", weight_bp: 60 },
    ],
  },
  {
    fund_ticker: "FZILX",
    name: "Fidelity ZERO International Index Fund",
    fund_type: "mutual_fund",
    top_holdings: [
      { ticker: "TSM", weight_bp: 215 },
      { ticker: "ASML", weight_bp: 95 },
      { ticker: "NVO", weight_bp: 90 },
      { ticker: "NESN.SW", weight_bp: 85 },
      { ticker: "TM", weight_bp: 70 },
      { ticker: "ROG.SW", weight_bp: 70 },
      { ticker: "SAP", weight_bp: 70 },
      { ticker: "AZN", weight_bp: 65 },
      { ticker: "SHEL", weight_bp: 65 },
      { ticker: "NVS", weight_bp: 60 },
    ],
  },
  {
    fund_ticker: "VEA",
    name: "Vanguard FTSE Developed Markets ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "ASML", weight_bp: 130 },
      { ticker: "NVO", weight_bp: 125 },
      { ticker: "NESN.SW", weight_bp: 115 },
      { ticker: "ROG.SW", weight_bp: 95 },
      { ticker: "TM", weight_bp: 95 },
      { ticker: "SAP", weight_bp: 95 },
      { ticker: "AZN", weight_bp: 90 },
      { ticker: "SHEL", weight_bp: 90 },
      { ticker: "NVS", weight_bp: 85 },
      { ticker: "HSBC", weight_bp: 75 },
    ],
  },
  {
    fund_ticker: "VWO",
    name: "Vanguard FTSE Emerging Markets ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "TSM", weight_bp: 770 },
      { ticker: "TCEHY", weight_bp: 380 },
      { ticker: "BABA", weight_bp: 240 },
      { ticker: "PDD", weight_bp: 175 },
      { ticker: "RELIANCE.NS", weight_bp: 165 },
      { ticker: "MEITUAN", weight_bp: 130 },
      { ticker: "INFY", weight_bp: 110 },
      { ticker: "HDB", weight_bp: 105 },
      { ticker: "HDFCBANK.NS", weight_bp: 105 },
      { ticker: "VALE", weight_bp: 90 },
    ],
  },
  {
    fund_ticker: "FSPSX",
    name: "Fidelity International Index Fund",
    fund_type: "mutual_fund",
    top_holdings: [
      { ticker: "ASML", weight_bp: 125 },
      { ticker: "NVO", weight_bp: 120 },
      { ticker: "NESN.SW", weight_bp: 115 },
      { ticker: "ROG.SW", weight_bp: 95 },
      { ticker: "TM", weight_bp: 90 },
      { ticker: "SAP", weight_bp: 90 },
      { ticker: "AZN", weight_bp: 90 },
      { ticker: "SHEL", weight_bp: 85 },
      { ticker: "NVS", weight_bp: 85 },
      { ticker: "HSBC", weight_bp: 70 },
    ],
  },
  // === Bonds (no equity overlap, but listed for completeness) =============
  {
    fund_ticker: "BND",
    name: "Vanguard Total Bond Market ETF",
    fund_type: "etf",
    top_holdings: [],
  },
  {
    fund_ticker: "BNDX",
    name: "Vanguard Total International Bond ETF",
    fund_type: "etf",
    top_holdings: [],
  },
  {
    fund_ticker: "AGG",
    name: "iShares Core U.S. Aggregate Bond ETF",
    fund_type: "etf",
    top_holdings: [],
  },
  {
    fund_ticker: "VTIP",
    name: "Vanguard Short-Term TIPS ETF",
    fund_type: "etf",
    top_holdings: [],
  },
  // === Schwab broad market ===============================================
  {
    fund_ticker: "SCHB",
    name: "Schwab US Broad Market ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 615 },
      { ticker: "MSFT", weight_bp: 580 },
      { ticker: "NVDA", weight_bp: 515 },
      { ticker: "AMZN", weight_bp: 360 },
      { ticker: "META", weight_bp: 235 },
      { ticker: "GOOGL", weight_bp: 200 },
      { ticker: "GOOG", weight_bp: 170 },
      { ticker: "TSLA", weight_bp: 155 },
      { ticker: "BRK.B", weight_bp: 140 },
      { ticker: "AVGO", weight_bp: 130 },
    ],
  },
  {
    fund_ticker: "SCHX",
    name: "Schwab US Large-Cap ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "AAPL", weight_bp: 690 },
      { ticker: "MSFT", weight_bp: 650 },
      { ticker: "NVDA", weight_bp: 590 },
      { ticker: "AMZN", weight_bp: 405 },
      { ticker: "META", weight_bp: 265 },
      { ticker: "GOOGL", weight_bp: 225 },
      { ticker: "GOOG", weight_bp: 190 },
      { ticker: "TSLA", weight_bp: 175 },
      { ticker: "BRK.B", weight_bp: 165 },
      { ticker: "AVGO", weight_bp: 150 },
    ],
  },
  {
    fund_ticker: "SCHF",
    name: "Schwab International Equity ETF",
    fund_type: "etf",
    top_holdings: [
      { ticker: "ASML", weight_bp: 125 },
      { ticker: "NVO", weight_bp: 120 },
      { ticker: "NESN.SW", weight_bp: 115 },
      { ticker: "ROG.SW", weight_bp: 95 },
      { ticker: "TM", weight_bp: 90 },
      { ticker: "SAP", weight_bp: 90 },
      { ticker: "AZN", weight_bp: 90 },
      { ticker: "SHEL", weight_bp: 85 },
      { ticker: "NVS", weight_bp: 85 },
      { ticker: "HSBC", weight_bp: 70 },
    ],
  },
];

/** Index by fund ticker for O(1) lookups. */
export const FUND_CONSTITUENTS_BY_TICKER: ReadonlyMap<string, FundConstituentEntry> = new Map(
  FUND_CONSTITUENTS_TABLE.map((f) => [f.fund_ticker, f]),
);
